import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import {
  Button,
  Input,
  Modal,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Typography,
  Radio,
  message,
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useApolloClient } from 'react-apollo';
import GET_DSP_INVENTORY_SOURCES from '../../../core/GraphQl/Queries/GET_DSP_INVENTORY_SOURCES';
import GET_DSP_INVENTORY_LISTS from '../../../core/GraphQl/Queries/GET_DSP_INVENTORY_LISTS';
import GET_NEXXEN_PUBLISHER_DEALS from '../../../core/GraphQl/Queries/GET_NEXXEN_PUBLISHER_DEALS';

const { Text } = Typography;
const { TabPane } = Tabs;

const CampaignRequestorInventoryDialog = ({
  visible,
  setVisible,
  onSaveInventoryConfig,
  selectedInventorySources = [],
  selectedInventoryLists = [],
  selectedPublisherDeals = [],
  campaignType,
}) => {
  const [activeTab, setActiveTab] = useState('sources');
  const [selectedSourceKeys, setSelectedSourceKeys] = useState([]);
  const [selectedListKeys, setSelectedListKeys] = useState([]);
  const [selectedDealKeys, setSelectedDealKeys] = useState([]);
  const [listActions, setListActions] = useState({});

  // Refs to track if selections have been initialized (prevent re-initialization)
  const sourcesInitializedRef = useRef(false);
  const listsInitializedRef = useRef(false);
  const dealsInitializedRef = useRef(false);

  // Ref to track if component is mounted (prevent memory leaks)
  const isMountedRef = useRef(true);

  const [inventorySources, setInventorySources] = useState([]);
  const [inventoryLists, setInventoryLists] = useState([]);
  const [publisherDeals, setPublisherDeals] = useState([]);

  const [loadingSources, setLoadingSources] = useState(false);
  const [loadingLists, setLoadingLists] = useState(false);
  const [loadingDeals, setLoadingDeals] = useState(false);
  const [sourceProgress, setSourceProgress] = useState('');
  const [listProgress, setListProgress] = useState('');
  const [dealProgress, setDealProgress] = useState('');

  const [sourceSearchTerm, setSourceSearchTerm] = useState('');
  const [listSearchTerm, setListSearchTerm] = useState('');
  const [dealSearchTerm, setDealSearchTerm] = useState('');

  const [sourcePage, setSourcePage] = useState(1);
  const [listPage, setListPage] = useState(1);
  const [dealPage, setDealPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const client = useApolloClient();

  // Map campaign type to media format for filtering
  const getMediaFormatForCampaignType = (type) => {
    switch (type) {
      case 8: // Video/CTV
        return 'video';
      case 1: // Display
      case 7: // External
      default:
        return 'display';
    }
  };

  const requiredMediaFormat = getMediaFormatForCampaignType(campaignType);

  // Fetch all inventory sources
  const fetchInventorySources = useCallback(async () => {
    if (!isMountedRef.current) return;
    setLoadingSources(true);
    setSourceProgress('Loading inventory sources...');
    let allSources = [];
    let offset = 0;
    const limit = 100;

    try {
      while (isMountedRef.current) {
        const { data } = await client.query({
          query: GET_DSP_INVENTORY_SOURCES,
          variables: { offset, limit, inventorySourceType: 'rtbe' },
          fetchPolicy: 'network-only',
        });

        if (!isMountedRef.current) return;

        const pageData = data?.dspInventorySources?.data || [];
        const total = data?.dspInventorySources?.queryTotal || 0;

        allSources = [...allSources, ...pageData];
        setSourceProgress(`Loaded ${allSources.length} of ${total} inventory sources...`);

        if (allSources.length >= total || pageData.length === 0) break;
        offset += limit;
      }

      if (isMountedRef.current) {
        setInventorySources(allSources);
      }
    } catch (error) {
      console.error('Error fetching inventory sources:', error);
      if (isMountedRef.current) {
        message.error('Failed to load inventory sources');
        setInventorySources([]);
      }
    } finally {
      if (isMountedRef.current) {
        setLoadingSources(false);
        setSourceProgress('');
      }
    }
  }, [client]);

  // Fetch all inventory lists (backend handles pagination)
  const fetchInventoryLists = useCallback(async () => {
    if (!isMountedRef.current) return;
    setLoadingLists(true);
    setListProgress('Loading inventory lists...');

    try {
      const { data } = await client.query({
        query: GET_DSP_INVENTORY_LISTS,
        fetchPolicy: 'network-only',
      });

      if (isMountedRef.current) {
        const allLists = data?.dspInventoryLists?.data || [];
        setInventoryLists(allLists);
      }
    } catch (error) {
      console.error('Error fetching inventory lists:', error);
      if (isMountedRef.current) {
        message.error('Failed to load inventory lists');
        setInventoryLists([]);
      }
    } finally {
      if (isMountedRef.current) {
        setLoadingLists(false);
        setListProgress('');
      }
    }
  }, [client]);

  // Fetch all publisher deals
  const fetchPublisherDeals = useCallback(async () => {
    if (!isMountedRef.current) return;
    setLoadingDeals(true);
    setDealProgress('Loading publisher deals...');
    let allDeals = [];
    let offset = 0;
    const limit = 100;

    try {
      while (isMountedRef.current) {
        const { data } = await client.query({
          query: GET_NEXXEN_PUBLISHER_DEALS,
          variables: { offset, limit },
          fetchPolicy: 'network-only',
        });

        if (!isMountedRef.current) return;

        const pageData = data?.nexxenPublisherDeals?.data || [];
        const total = data?.nexxenPublisherDeals?.queryTotal || 0;

        allDeals = [...allDeals, ...pageData];
        setDealProgress(`Loaded ${allDeals.length} of ${total} publisher deals...`);

        if (allDeals.length >= total || pageData.length === 0) break;
        offset += limit;
      }

      if (isMountedRef.current) {
        setPublisherDeals(allDeals);
      }
    } catch (error) {
      console.error('Error fetching publisher deals:', error);
      if (isMountedRef.current) {
        message.error('Failed to load publisher deals');
        setPublisherDeals([]);
      }
    } finally {
      if (isMountedRef.current) {
        setLoadingDeals(false);
        setDealProgress('');
      }
    }
  }, [client]);

  // Set mounted ref on mount, clear on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Load data when modal opens
  useEffect(() => {
    if (visible) {
      if (inventorySources.length === 0) fetchInventorySources();
      if (inventoryLists.length === 0) fetchInventoryLists();
      if (publisherDeals.length === 0) fetchPublisherDeals();
    }
  }, [visible, inventorySources.length, inventoryLists.length, publisherDeals.length, fetchInventorySources, fetchInventoryLists, fetchPublisherDeals]);

  // Reset initialization refs when dialog closes
  useEffect(() => {
    if (!visible) {
      sourcesInitializedRef.current = false;
      listsInitializedRef.current = false;
      dealsInitializedRef.current = false;
    }
  }, [visible]);

  // Initialize selections from props - ONCE per data type when data loads
  useEffect(() => {
    if (visible) {
      // Initialize sources - only ONCE when source data has loaded
      if (inventorySources.length > 0 && !sourcesInitializedRef.current) {
        sourcesInitializedRef.current = true;
        if (selectedInventorySources?.length > 0) {
          const keys = selectedInventorySources
            .map((s) => inventorySources.find((src) => src.id === s.sourceId)?.id)
            .filter(Boolean);
          setSelectedSourceKeys(keys);
        } else {
          setSelectedSourceKeys([]);
        }
      }

      // Initialize lists with actions - only ONCE when list data has loaded
      if (inventoryLists.length > 0 && !listsInitializedRef.current) {
        listsInitializedRef.current = true;
        if (selectedInventoryLists?.length > 0) {
          const keys = selectedInventoryLists
            .map((l) => inventoryLists.find((lst) => lst.id === l.listId)?.id)
            .filter(Boolean);
          setSelectedListKeys(keys);
          const actions = {};
          selectedInventoryLists.forEach((l) => {
            actions[l.listId] = l.action || 'include';
          });
          setListActions(actions);
        } else {
          setSelectedListKeys([]);
          setListActions({});
        }
      }

      // Initialize deals - only ONCE when deal data has loaded
      if (publisherDeals.length > 0 && !dealsInitializedRef.current) {
        dealsInitializedRef.current = true;
        if (selectedPublisherDeals?.length > 0) {
          const keys = selectedPublisherDeals
            .map((d) => publisherDeals.find((deal) => deal.dealId === d.dealId)?.id)
            .filter(Boolean);
          setSelectedDealKeys(keys);
        } else {
          setSelectedDealKeys([]);
        }
      }
    }
  }, [visible, selectedInventorySources, selectedInventoryLists, selectedPublisherDeals, inventorySources, inventoryLists, publisherDeals]);

  const handleSave = () => {
    // Build sources from selections (or preserve original if data not loaded)
    const sources = inventorySources.length > 0
      ? inventorySources
          .filter((s) => selectedSourceKeys.includes(s.id))
          .map((s) => ({
            sourceId: s.id,
            name: s.name,
          }))
      : selectedInventorySources || [];

    // Build lists from selections (or preserve original if data not loaded)
    const lists = inventoryLists.length > 0
      ? inventoryLists
          .filter((l) => selectedListKeys.includes(l.id))
          .map((l) => ({
            listId: l.id,
            name: l.name,
            action: listActions[l.id] || 'include',
          }))
      : selectedInventoryLists || [];

    // Build deals from selections (or preserve original if data not loaded)
    const deals = publisherDeals.length > 0
      ? publisherDeals
          .filter((d) => selectedDealKeys.includes(d.id))
          .map((d) => ({
            id: d.id,
            dealId: d.dealId,
            name: d.name,
            cpmBid: d.price || null,
          }))
      : selectedPublisherDeals || [];

    onSaveInventoryConfig({ sources, lists, deals });
    setVisible(false);
  };

  // Filter sources by search
  const filteredSources = useMemo(() => {
    if (!sourceSearchTerm) return inventorySources;
    return inventorySources.filter((s) =>
      s.name?.toLowerCase().includes(sourceSearchTerm.toLowerCase())
    );
  }, [inventorySources, sourceSearchTerm]);

  // Filter lists by search
  const filteredLists = useMemo(() => {
    if (!listSearchTerm) return inventoryLists;
    return inventoryLists.filter((l) =>
      l.name?.toLowerCase().includes(listSearchTerm.toLowerCase())
    );
  }, [inventoryLists, listSearchTerm]);

  // Filter deals by search and media format
  const filteredDeals = useMemo(() => {
    let filtered = publisherDeals;

    if (requiredMediaFormat) {
      filtered = filtered.filter((deal) =>
        !deal.mediaFormat || deal.mediaFormat === requiredMediaFormat
      );
    }

    if (dealSearchTerm) {
      filtered = filtered.filter((d) =>
        d.name?.toLowerCase().includes(dealSearchTerm.toLowerCase()) ||
        d.dealId?.toLowerCase().includes(dealSearchTerm.toLowerCase())
      );
    }

    return filtered;
  }, [publisherDeals, dealSearchTerm, requiredMediaFormat]);

  // Pagination helpers
  const paginateSources = useMemo(() => {
    const start = (sourcePage - 1) * pageSize;
    return filteredSources.slice(start, start + pageSize);
  }, [filteredSources, sourcePage, pageSize]);

  const paginateLists = useMemo(() => {
    const start = (listPage - 1) * pageSize;
    return filteredLists.slice(start, start + pageSize);
  }, [filteredLists, listPage, pageSize]);

  const paginateDeals = useMemo(() => {
    const start = (dealPage - 1) * pageSize;
    return filteredDeals.slice(start, start + pageSize);
  }, [filteredDeals, dealPage, pageSize]);

  // Source columns
  const sourceColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 300,
      ellipsis: true,
    },
    {
      title: 'Type',
      dataIndex: 'inventorySourceType',
      key: 'inventorySourceType',
      width: 100,
      render: (type) => <Tag color="blue">{type || 'rtbe'}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => (
        <Tag color={status === 'active' ? 'green' : 'default'}>
          {status || 'active'}
        </Tag>
      ),
    },
  ];

  // List columns
  const listColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 250,
      ellipsis: true,
    },
    {
      title: 'Usage',
      dataIndex: 'inventoryUsage',
      key: 'inventoryUsage',
      width: 80,
      render: (usage) => <Tag color={usage === 'apps' ? 'purple' : 'cyan'}>{usage || 'sites'}</Tag>,
    },
    {
      title: 'Items',
      dataIndex: 'itemCount',
      key: 'itemCount',
      width: 80,
      render: (count) => count || '-',
    },
    {
      title: 'Action',
      key: 'action',
      width: 150,
      render: (_, record) => (
        selectedListKeys.includes(record.id) ? (
          <Radio.Group
            size="small"
            value={listActions[record.id] || 'include'}
            onChange={(e) => setListActions({ ...listActions, [record.id]: e.target.value })}
          >
            <Radio.Button value="include">Include</Radio.Button>
            <Radio.Button value="exclude">Exclude</Radio.Button>
          </Radio.Group>
        ) : (
          <Text type="secondary">Select to configure</Text>
        )
      ),
    },
  ];

  // Deal columns
  const dealColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 220,
      ellipsis: true,
    },
    {
      title: 'Deal ID',
      dataIndex: 'dealId',
      key: 'dealId',
      width: 150,
      ellipsis: true,
    },
    {
      title: 'Format',
      dataIndex: 'mediaFormat',
      key: 'mediaFormat',
      width: 90,
      render: (format) => {
        const color = format === 'video' ? 'blue' : format === 'audio' ? 'purple' : 'green';
        return format ? <Tag color={color}>{format}</Tag> : '-';
      },
    },
    {
      title: 'CPM',
      dataIndex: 'price',
      key: 'price',
      width: 80,
      render: (value) => (value ? `$${value.toFixed(2)}` : '-'),
    },
    {
      title: 'Status',
      dataIndex: 'workflowStatus',
      key: 'workflowStatus',
      width: 90,
      render: (status) => (
        <Tag color={status === 'approved' ? 'green' : 'default'}>
          {status || 'Unknown'}
        </Tag>
      ),
    },
  ];

  const getTotalSelections = () => {
    return selectedSourceKeys.length + selectedListKeys.length + selectedDealKeys.length;
  };

  return (
    <Modal
      title={
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingRight: 20,
          }}
        >
          <span>INVENTORY & SUPPLY CONFIGURATION</span>
          <div>
            <Button
              type="primary"
              onClick={handleSave}
            >
              Save Configuration ({getTotalSelections()} items)
            </Button>
          </div>
        </div>
      }
      visible={visible}
      onCancel={() => setVisible(false)}
      footer={null}
      width={1000}
    >
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane
          tab={
            <span>
              Inventory Sources
              {selectedSourceKeys.length > 0 && (
                <Tag color="blue" style={{ marginLeft: 8 }}>{selectedSourceKeys.length}</Tag>
              )}
            </span>
          }
          key="sources"
        >
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Text type="secondary">
              Select SSPs/exchanges to use for this line item. If none selected, default exchanges will be used.
            </Text>
            <Input
              placeholder="Search inventory sources..."
              prefix={<SearchOutlined />}
              value={sourceSearchTerm}
              onChange={(e) => { setSourceSearchTerm(e.target.value); setSourcePage(1); }}
              allowClear
            />
            {loadingSources ? (
              <Space direction="vertical" style={{ width: '100%', padding: 40 }} align="center">
                <Spin />
                <Text type="secondary">{sourceProgress || 'Loading...'}</Text>
              </Space>
            ) : (
              <Table
                rowKey="id"
                rowSelection={{
                  type: 'checkbox',
                  selectedRowKeys: selectedSourceKeys,
                  onChange: setSelectedSourceKeys,
                  preserveSelectedRowKeys: true,
                }}
                columns={sourceColumns}
                dataSource={paginateSources}
                pagination={{
                  current: sourcePage,
                  pageSize,
                  total: filteredSources.length,
                  showSizeChanger: true,
                  pageSizeOptions: ['10', '20', '50'],
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
                  onChange: (page, size) => {
                    if (size !== pageSize) { setPageSize(size); setSourcePage(1); }
                    else setSourcePage(page);
                  },
                }}
                scroll={{ y: 350 }}
                size="small"
              />
            )}
          </Space>
        </TabPane>

        <TabPane
          tab={
            <span>
              Inventory Lists
              {selectedListKeys.length > 0 && (
                <Tag color="green" style={{ marginLeft: 8 }}>{selectedListKeys.length}</Tag>
              )}
            </span>
          }
          key="lists"
        >
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Text type="secondary">
              Select site/app lists to include or exclude for this line item.
            </Text>
            <Input
              placeholder="Search inventory lists..."
              prefix={<SearchOutlined />}
              value={listSearchTerm}
              onChange={(e) => { setListSearchTerm(e.target.value); setListPage(1); }}
              allowClear
            />
            {loadingLists ? (
              <Space direction="vertical" style={{ width: '100%', padding: 40 }} align="center">
                <Spin />
                <Text type="secondary">{listProgress || 'Loading...'}</Text>
              </Space>
            ) : (
              <Table
                rowKey="id"
                rowSelection={{
                  type: 'checkbox',
                  selectedRowKeys: selectedListKeys,
                  onChange: setSelectedListKeys,
                  preserveSelectedRowKeys: true,
                }}
                columns={listColumns}
                dataSource={paginateLists}
                pagination={{
                  current: listPage,
                  pageSize,
                  total: filteredLists.length,
                  showSizeChanger: true,
                  pageSizeOptions: ['10', '20', '50'],
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
                  onChange: (page, size) => {
                    if (size !== pageSize) { setPageSize(size); setListPage(1); }
                    else setListPage(page);
                  },
                }}
                scroll={{ y: 350 }}
                size="small"
              />
            )}
          </Space>
        </TabPane>

        <TabPane
          tab={
            <span>
              Publisher Deals
              {selectedDealKeys.length > 0 && (
                <Tag color="purple" style={{ marginLeft: 8 }}>{selectedDealKeys.length}</Tag>
              )}
            </span>
          }
          key="deals"
        >
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Space direction="vertical" size={4}>
              <Text type="secondary">
                Select private marketplace deals for this line item.
              </Text>
              {requiredMediaFormat && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Showing only <Tag color={requiredMediaFormat === 'video' ? 'blue' : 'green'} style={{ margin: 0 }}>{requiredMediaFormat}</Tag> deals compatible with your campaign type.
                </Text>
              )}
            </Space>
            <Input
              placeholder="Search deals by name or deal ID..."
              prefix={<SearchOutlined />}
              value={dealSearchTerm}
              onChange={(e) => { setDealSearchTerm(e.target.value); setDealPage(1); }}
              allowClear
            />
            {loadingDeals ? (
              <Space direction="vertical" style={{ width: '100%', padding: 40 }} align="center">
                <Spin />
                <Text type="secondary">{dealProgress || 'Loading...'}</Text>
              </Space>
            ) : (
              <Table
                rowKey="id"
                rowSelection={{
                  type: 'checkbox',
                  selectedRowKeys: selectedDealKeys,
                  onChange: setSelectedDealKeys,
                  preserveSelectedRowKeys: true,
                }}
                columns={dealColumns}
                dataSource={paginateDeals}
                pagination={{
                  current: dealPage,
                  pageSize,
                  total: filteredDeals.length,
                  showSizeChanger: true,
                  pageSizeOptions: ['10', '20', '50'],
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
                  onChange: (page, size) => {
                    if (size !== pageSize) { setPageSize(size); setDealPage(1); }
                    else setDealPage(page);
                  },
                }}
                scroll={{ y: 350 }}
                size="small"
              />
            )}
          </Space>
        </TabPane>
      </Tabs>
    </Modal>
  );
};

export default CampaignRequestorInventoryDialog;
